// JSON over fetch, with the server's error message surfaced as-is.

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string, public body?: unknown) { super(message) }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers = new Headers(init.headers)
  let body = init.body
  if (init.json !== undefined) {
    headers.set('content-type', 'application/json')
    body = JSON.stringify(init.json)
  }
  const res = await fetch(path, { ...init, headers, body, credentials: 'same-origin' })
  const text = await res.text()
  let data: unknown = null
  try { data = text ? JSON.parse(text) : null } catch { data = text }
  if (!res.ok) {
    const d = data as { error?: string; code?: string } | null
    throw new ApiError(res.status, d?.error ?? `Request failed (${res.status})`, d?.code, data)
  }
  return data as T
}

/** Upload with progress (fetch has no upload progress). */
export function upload<T>(path: string, file: File, onProgress: (fraction: number) => void): { promise: Promise<T>; abort: () => void } {
  const xhr = new XMLHttpRequest()
  const promise = new Promise<T>((resolve, reject) => {
    xhr.open('POST', path)
    xhr.setRequestHeader('x-file-name', encodeURIComponent(file.name))
    xhr.setRequestHeader('content-type', file.type || 'application/octet-stream')
    xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress(e.loaded / e.total) }
    xhr.onload = () => {
      let data: { error?: string } = {}
      try { data = JSON.parse(xhr.responseText) } catch { /* not json */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T)
      else reject(new ApiError(xhr.status, data.error ?? `Upload failed (${xhr.status})`))
    }
    xhr.onerror = () => reject(new ApiError(0, 'The upload was interrupted — check your connection and try again'))
    xhr.onabort = () => reject(new ApiError(0, 'Upload cancelled'))
    xhr.send(file)
  })
  return { promise, abort: () => xhr.abort() }
}
